import { useState } from "react";
import { AlertTriangle, RadioTower, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ helpers shared by the shell + Live Count Feed page */
export const fmtClock = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kuala_Lumpur" });
};

export const silentText = (s) => {
  if (!s) return "";
  if (!s.ever_reported) return "never connected";
  const m = s.silent_minutes;
  if (m === null || m === undefined) return "silent";
  return m < 60 ? `silent ${m} min` : `silent ${Math.floor(m / 60)} h ${m % 60} min`;
};

export const siteFeed = (feed, siteId) => (feed?.sites || []).find((s) => s.id === siteId) || null;

export const agoText = (minutes) => {
  if (minutes === null || minutes === undefined) return "no delivery yet";
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const h = Math.floor(minutes / 60);
  return h < 48 ? `${h} h ago` : `${Math.floor(h / 24)} d ago`;
};

const FEED_STYLE = {
  live: { cls: "border-success/40 bg-success/10 text-success", dot: "bg-success animate-pulse", label: "Live feed" },
  silent: { cls: "border-warning/40 bg-warning/10 text-warning", dot: "bg-warning", label: "Feed silent" },
  idle: { cls: "border-border/70 bg-secondary/60 text-muted-foreground", dot: "bg-muted-foreground/60", label: "Feed idle" },
  not_connected: { cls: "border-border/70 bg-secondary/60 text-muted-foreground", dot: "bg-muted-foreground/40", label: "Feed not connected" },
};

/** Pill showing the state of the 10-minute live count feed. */
export const FeedPill = ({ feed, onNavigate, compact = false }) => {
  const state = feed?.state || "not_connected";
  const st = FEED_STYLE[state] || FEED_STYLE.not_connected;
  const silentCount = (feed?.silent_sites || []).length;
  const stoppedCount = (feed?.stopped_sites || []).length;
  const tip =
    state === "live"
      ? `All ${feed.site_count || 8} stores posting · last delivery ${agoText(feed.minutes_since_delivery)}`
      : state === "silent"
        ? `${feed.headline}. Flagged after ${feed.silent_after_minutes} min without a delivery during trading hours (${feed.myt_time} MYT now).${(feed.silent_names || []).length ? ` Silent: ${feed.silent_names.join(", ")}.` : ""}`
        : state === "idle"
          ? `Stores closed · last delivery ${agoText(feed.minutes_since_delivery)}`
          : "No live deliveries received yet. Historical CSV exports are shown. Connect the counting project from the Live Count Feed page.";
  const label = state === "silent" ? (stoppedCount ? `Feed silent · ${stoppedCount} stopped` : `Feed silent · ${silentCount} of ${feed.site_count || 8}`) : st.label;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid="feed-pill"
          data-state={state}
          data-silent-count={silentCount}
          onClick={() => onNavigate?.()}
          className={cn("inline-flex items-center gap-1.5 rounded-md border font-mono text-[11px] py-1 px-2.5 focus-gold hover:brightness-110 transition-[filter] duration-200", st.cls, !onNavigate && "cursor-default")}
        >
          <span className={cn("h-1.5 w-1.5 rounded-full", st.dot, state === "silent" && stoppedCount > 0 && "animate-pulse")} />
          {compact ? <RadioTower size={12} /> : label}
          {!compact && state === "live" && <span className="text-muted-foreground">· {agoText(feed.minutes_since_delivery)}</span>}
          {compact && state === "silent" && <span>{silentCount}</span>}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs text-xs">{tip}</TooltipContent>
    </Tooltip>
  );
};

/** Per-store feed state dot (live / silent / idle / not connected). */
export const StoreFeedDot = ({ entry, withLabel = false, className }) => {
  if (!entry) return null;
  const m = {
    live: { dot: "bg-success", text: "text-success", label: "live" },
    silent: { dot: "bg-warning", text: "text-warning", label: entry.ever_reported ? `silent ${entry.silent_minutes} min` : "not connected" },
    idle: { dot: "bg-muted-foreground/60", text: "text-muted-foreground", label: "idle" },
    not_connected: { dot: "bg-muted-foreground/40", text: "text-muted-foreground/70", label: "not connected" },
  }[entry.state] || { dot: "bg-muted-foreground/40", text: "text-muted-foreground", label: entry.state };
  return (
    <span className={cn("inline-flex items-center gap-1.5 font-mono text-[11px]", m.text, className)} data-testid={`store-feed-${entry.id}`} data-state={entry.state}>
      <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", m.dot, entry.state === "live" && "animate-pulse")} />
      {withLabel && m.label}
    </span>
  );
};

/**
 * Dashboard banner: stores that stopped posting / never connected during trading hours.
 * Dismissable for the session; re-appears when the set of silent stores changes.
 */
export const FeedAlertBanner = ({ feed, siteIds = [], onNavigate, className, canOpenFeed = false }) => {
  const [dismissedKey, setDismissedKey] = useState(null);
  if (!feed || feed.state !== "silent") return null;
  const stopped = feed.stopped_sites || [];
  const silent = feed.silent_sites || [];
  const key = `${stopped.join(",")}|${silent.length}`;
  if (dismissedKey === key) return null;
  const allNever = stopped.length === 0;
  const inScope = (feed.sites || []).filter((s) => siteIds.includes(s.id) && s.state === "silent");
  return (
    <div
      data-testid="feed-alert-banner"
      data-stopped={stopped.length}
      data-silent={silent.length}
      className={cn("rounded-xl border px-4 py-3 md:px-5 md:py-4 flex items-start gap-3", allNever ? "border-border/70 bg-secondary/50" : "border-warning/40 bg-warning/10", className)}
    >
      <div className={cn("h-9 w-9 rounded-lg border flex items-center justify-center shrink-0", allNever ? "border-border/70 bg-secondary/60 text-muted-foreground" : "border-warning/40 bg-warning/15 text-warning")}>
        {allNever ? <RadioTower size={16} /> : <AlertTriangle size={16} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium" data-testid="feed-alert-headline">{feed.headline}</div>
        <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
          {allNever
            ? `It is ${feed.myt_time} MYT, inside trading hours (${feed.trading_hours_myt?.[0]}:00–${feed.trading_hours_myt?.[1]}:00). Figures below come from CSV history until the counting project starts posting.`
            : `No delivery for more than ${feed.silent_after_minutes} minutes during trading hours (${feed.myt_time} MYT). Stopped: ${(feed.sites || []).filter((s) => stopped.includes(s.id)).map((s) => `${s.name} (${silentText(s)})`).join(", ")}.`}
        </div>
        {inScope.length > 0 && siteIds.length < 8 && (
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-warning/35 bg-warning/10 px-2 py-1 font-mono text-[11px] text-warning" data-testid="feed-alert-current-site">
            <span className="h-1.5 w-1.5 rounded-full bg-warning" />
            In this view: {inScope.map((s) => `${s.name} · ${silentText(s)}`).join(" · ")}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {canOpenFeed && (
          <Button variant="ghost" size="sm" className="h-8 text-xs gap-1 hidden sm:inline-flex" data-testid="feed-alert-open-brief" onClick={() => onNavigate?.()}>
            Live feed <ArrowRight size={13} />
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Dismiss" data-testid="feed-alert-dismiss" onClick={() => setDismissedKey(key)}>
          <X size={14} />
        </Button>
      </div>
    </div>
  );
};
